import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Empty, Table, Tag } from 'antd';
import { formatDate, DATE_FORMATS } from '@/utils/formUtils';
import { groupHistoryChanges } from '@/shared/history';
import { getUserFullName } from '@/utils/utils';

/**
 * One `label: before → after` line.
 */
const ChangeLine = ({ line }) => (
  <div className="flex flex-wrap items-baseline gap-x-1.5 text-sm leading-6">
    <span className="font-medium text-gray-700">
      {line.label}:
    </span>
    <span className="text-gray-500 line-through decoration-gray-400">
      {line.from}
    </span>
    <span className="text-gray-400">→</span>
    <span className="font-medium text-gray-900">{line.to}</span>
  </div>
);

/**
 * Every line of one audit row, grouped by section.
 *
 * Arrays and populated references render as their summary here rather than
 * expanding: this tab is a table, and one company row expands to roughly 590
 * leaf lines. The timeline tab is where a reader opens a single entry.
 */
const ChangeCell = ({ changes, modelName, t, locale }) => {
  const grouped = useMemo(
    () => groupHistoryChanges(changes, modelName, { t, locale }),
    [changes, modelName, t, locale]
  );

  if (!grouped.totalLines) {
    return <span className="text-sm text-gray-400">{t('ui.noChangeLines')}</span>;
  }

  return (
    <div className="space-y-2">
      {grouped.sections.map((section) => (
        <div key={section.key}>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {section.label}
          </div>
          {section.lines.map((line) => (
            <ChangeLine key={line.id} line={line} />
          ))}
          {[...section.arrays, ...section.references].map((group) => (
            <div key={group.id} className="text-sm text-gray-600">
              <span className="font-medium">{group.label}:</span> {group.summary}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/** Lines a record card shows before "show all". */
const PROFILE_LINES = 10;
/** Record cards per page on the profile. */
const PROFILE_PAGE = 10;

/**
 * Profile page: one audit record as a card — when, who and which sections on
 * top, then each section as a small Field / Before / After table.
 */
const ProfileRecord = ({ record, modelName, t, locale }) => {
  const [showAll, setShowAll] = useState(false);
  const grouped = useMemo(
    () => groupHistoryChanges(record.changes, modelName, { t, locale }),
    [record.changes, modelName, t, locale]
  );
  const { user } = record;
  const userName = user
    ? getUserFullName(user.firstName, user.lastName)
    : t('ui.system');
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '·';

  // Rows per section; arrays and references as their one-line summary.
  const sections = grouped.sections.map((section) => ({
    key: section.key,
    label: section.label,
    rows: [
      ...section.lines.map((line) => ({
        id: line.id,
        label: line.label,
        from: line.from,
        to: line.to,
      })),
      ...[...section.arrays, ...section.references].map((group) => ({
        id: group.id,
        label: group.label,
        from: null,
        to: group.summary,
      })),
    ],
  }));
  const total = sections.reduce((sum, section) => sum + section.rows.length, 0);

  // Cut after PROFILE_LINES rows in total, keeping whole section headers.
  let left = showAll ? Infinity : PROFILE_LINES;
  const shown = sections
    .map((section) => {
      const rows = section.rows.slice(0, Math.max(left, 0));
      left -= rows.length;
      return { ...section, rows };
    })
    .filter((section) => section.rows.length);

  return (
    <li className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-zinc-100 px-3.5 py-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-accent text-[10px] font-bold text-white">
          {initials}
        </span>
        <span className="text-[13px] font-bold text-zinc-800">{userName}</span>
        <span className="text-xs text-zinc-400">
          {formatDate(record.timestamp, DATE_FORMATS.DISPLAY_DOTS)}{' '}
          {formatDate(record.timestamp, DATE_FORMATS.TIME_ONLY)}
        </span>
        <span className="flex flex-wrap gap-1">
          {grouped.sections.map((section) => (
            <Tag key={section.key} color="blue" className="!m-0">
              {section.label}
            </Tag>
          ))}
        </span>
        <span className="ml-auto text-xs font-semibold text-zinc-500">
          {t('ui.fieldCount', { count: total })}
        </span>
      </header>

      {!total ? (
        <div className="px-3.5 py-3 text-sm text-zinc-400">
          {t('ui.noChangeLines')}
        </div>
      ) : (
        <div className="grid gap-3 px-3.5 py-3">
          {shown.map((section) => (
            <div key={section.key}>
              <div className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
                {section.label}
              </div>
              <div className="overflow-hidden rounded-lg border border-zinc-100">
                <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] bg-zinc-50 px-2.5 py-1 text-[11px] font-semibold text-zinc-500">
                  <span>{t('ui.field')}</span>
                  <span>{t('ui.previousValue')}</span>
                  <span>{t('ui.newValue')}</span>
                </div>
                {section.rows.map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 border-t border-zinc-100 px-2.5 py-1.5 text-[12.5px]"
                  >
                    <span className="break-words font-medium text-zinc-700">
                      {row.label}
                    </span>
                    <span className="break-words text-zinc-400 line-through decoration-zinc-300">
                      {row.from ?? ''}
                    </span>
                    <span className="break-words font-semibold text-zinc-900">
                      {row.to}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {total > PROFILE_LINES && (
            <Button
              type="link"
              size="small"
              className="w-max !px-0"
              onClick={() => setShowAll((open) => !open)}
            >
              {showAll
                ? t('ui.hideDetails')
                : t('ui.showAllLines', { count: total })}
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * AuditLogsTab
 *
 * The same changes as the timeline, in a sortable table — one row per recorded
 * change, with the affected sections tagged.
 */
export default function AuditLogsTab({ data, loading, modelName, variant }) {
  const { t, i18n } = useTranslation('history');
  const locale = i18n.language || 'sr';
  const [visibleCount, setVisibleCount] = useState(PROFILE_PAGE);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <Empty description={t('ui.noHistory')} className="my-8" />;
  }

  if (variant === 'profile') {
    const sorted = [...data].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
    const visible = sorted.slice(0, visibleCount);
    return (
      <div className="grid gap-3 pt-1">
        <ol className="grid gap-3">
          {visible.map((record) => (
            <ProfileRecord
              key={record.id}
              record={record}
              modelName={modelName}
              t={t}
              locale={locale}
            />
          ))}
        </ol>
        <div className="flex items-center justify-center gap-3 pb-2 text-xs text-zinc-500">
          <span>
            {t('ui.showing', { shown: visible.length, total: sorted.length })}
          </span>
          {visible.length < sorted.length && (
            <Button
              size="small"
              onClick={() => setVisibleCount((count) => count + PROFILE_PAGE)}
            >
              {t('ui.loadMore')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: t('field.updatedAt'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) =>
        `${formatDate(timestamp, DATE_FORMATS.DISPLAY_DOTS)} ${formatDate(
          timestamp,
          DATE_FORMATS.TIME_ONLY
        )}`,
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      width: 170,
    },
    {
      title: t('ui.user'),
      dataIndex: 'user',
      key: 'user',
      render: (user) =>
        user ? getUserFullName(user.firstName, user.lastName) : t('ui.system'),
      width: 160,
    },
    {
      title: t('ui.sectionFilter'),
      key: 'sections',
      width: 200,
      render: (_, record) => {
        const grouped = groupHistoryChanges(record.changes, modelName, {
          t,
          locale,
        });
        return (
          <div className="flex flex-wrap gap-1">
            {grouped.sections.map((section) => (
              <Tag key={section.key} color="blue">
                {section.label}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t('ui.auditLogs'),
      dataIndex: 'changes',
      key: 'changes',
      render: (changes) => (
        <ChangeCell
          changes={changes}
          modelName={modelName}
          t={t}
          locale={locale}
        />
      ),
    },
  ];

  return (
    <div className="p-2">
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => t('ui.changeCount', { count: total }),
        }}
      />
    </div>
  );
}
